import { useTranslation } from 'react-i18next'
import { IconButton, Tooltip, CircularProgress } from '@mui/material'
import RefreshIcon from '@mui/icons-material/Refresh'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { ADMIN_ROLE_ID } from '@/config/roles'
import { useAppStore } from '@/store/appStore'
import { useAuthStore } from '@/stores/authStore'
import { refreshContentBundle } from '@/api/contentApi'
import { clearMediaCache } from '@/db/viewerDb'

export function ForceRefreshBundleButton() {
  const { t } = useTranslation()
  const { data: user } = useCurrentUser()
  const isAdmin = user?.roles.some(r => r.id === ADMIN_ROLE_ID) ?? false

  const { selectedLanguage, showDraftContent, isOffline, setBundleRefreshing } = useAppStore()
  const language = selectedLanguage ?? 'en'
  const isDraft = showDraftContent && !isOffline

  const loadIdentity = useAuthStore((s) => s.loadIdentity)
  const queryClient = useQueryClient()

  const mutation = useMutation({
    onMutate: () => setBundleRefreshing(true),
    mutationFn: refreshContentBundle,
    onSuccess: async () => {
      // Clear stale cached images so they are re-fetched alongside the new bundle.
      clearMediaCache().catch((err) =>
        console.warn('[ForceRefresh] Failed to clear media cache:', err),
      )
      // Invalidate languages so newly-added languages appear in the selector immediately.
      queryClient.invalidateQueries({ queryKey: ['languages'] })
      // Refresh the current user (picks up new roles, draft preview status, etc.)
      // and the content bundle in parallel; loading overlay stays until both complete.
      await Promise.all([
        loadIdentity(),
        queryClient.refetchQueries({
          queryKey: isDraft ? ['content-bundle-draft', language] : ['content-bundle', language],
        }),
      ])
      setBundleRefreshing(false)
    },
    onError: () => {
      setBundleRefreshing(false)
      toast.error(t('practiceContent.refreshFailed'))
    },
  })

  if (!isAdmin) return null

  return (
    <Tooltip title={t('practiceContent.forceRefreshBundle')} placement="bottom">
      <span>
        <IconButton
          onClick={() => mutation.mutate()}
          disabled={mutation.isPending}
          aria-label={t('practiceContent.forceRefreshBundle')}
          size="small"
          sx={{
            color: 'var(--color-on-dark-55)',
            '&:hover': { color: 'var(--color-white)', backgroundColor: 'var(--color-on-dark-10)' },
          }}
        >
          {mutation.isPending ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon fontSize="small" />}
        </IconButton>
      </span>
    </Tooltip>
  )
}
